import { getHelpDescription } from './help/help-content';

/** Tooltip copy for primary UI metric labels. */
export const AI_MENTIONS_TOOLTIP = getHelpDescription('ai_mentions');

export const TEXT_OCCURRENCES_TOOLTIP = getHelpDescription('text_occurrences');

export interface AiMentionsSource {
  aiMentions?: number | null;
  /** @deprecated Use aiMentions */
  appearances?: number | null;
  /** @deprecated Use aiMentions — per-prompt scope */
  seenInProviders?: number | null;
}

export interface TextOccurrencesSource {
  textOccurrences?: number | null;
  /** @deprecated Use textOccurrences */
  totalMentions?: number | null;
  /** @deprecated Use textOccurrences — competitor row legacy */
  mentions?: number | null;
}

/**
 * Resolves canonical AI Mentions count.
 * Never falls back to text-occurrence fields (totalMentions / mentions).
 */
export function resolveAiMentions(source: AiMentionsSource): number {
  if (source.aiMentions != null && Number.isFinite(source.aiMentions)) {
    return source.aiMentions;
  }
  if (source.appearances != null && Number.isFinite(source.appearances)) {
    return source.appearances;
  }
  if (source.seenInProviders != null && Number.isFinite(source.seenInProviders)) {
    return source.seenInProviders;
  }
  return 0;
}

/** Resolves text repetition count (advanced / admin only). */
export function resolveTextOccurrences(source: TextOccurrencesSource): number {
  if (source.textOccurrences != null && Number.isFinite(source.textOccurrences)) {
    return source.textOccurrences;
  }
  if (source.totalMentions != null && Number.isFinite(source.totalMentions)) {
    return source.totalMentions;
  }
  if (source.mentions != null && Number.isFinite(source.mentions)) {
    return source.mentions;
  }
  return 0;
}

export function sumAiMentions<T extends AiMentionsSource>(items: T[]): number {
  return items.reduce((sum, item) => sum + resolveAiMentions(item), 0);
}

export function averageAiMentions<T extends AiMentionsSource>(items: T[]): number | null {
  if (!items.length) return null;
  return Math.round(sumAiMentions(items) / items.length);
}

/** Tooltip copy for AI Usage %. */
export const AI_USAGE_TOOLTIP = getHelpDescription('ai_usage');

/**
 * Share of executions where the brand appeared at least once.
 * AI Usage % = aiMentions / totalExecutions × 100
 */
export function computeAiUsagePercent(
  aiMentions: number,
  totalExecutions: number,
): number | null {
  if (totalExecutions <= 0 || !Number.isFinite(aiMentions)) return null;
  return Math.round((aiMentions / totalExecutions) * 100);
}

export function formatAiUsagePercent(
  aiMentions: number,
  totalExecutions: number,
): string {
  const value = computeAiUsagePercent(aiMentions, totalExecutions);
  return value == null ? '—' : `${value}%`;
}
